import React, { useRef, useMemo } from 'react';
import _ from 'lodash';
import { Button } from '_ui-core-js/React/components/Button.jsx';
import { BackgroundMedia } from '_ui-core-js/React/components/BackgroundMedia.jsx';
import { CssAnimation } from '_ui-core-js/React/components/CssAnimation.jsx';
import { useWindowSize } from '_ui-core-js/React/hooks/useWindowSize.js';
import { useExternalElementSize } from '_ui-core-js/React/hooks/useExternalElementSize.js';
import { AnimatedTextHeader } from '_ui-core-js/React/components/AnimatedTextHeader.jsx';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { AtmosphereImage } from '../../partials/AtmosphereImage.jsx';
import { LBX_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { constants } from '../../constants/index.js';
import { getGreeting, getHeightParams } from '../../helpers/personalisedHero.js';

const gtmComponentGroup = CORE_GTM_TRACKING_VALUES.componentGroup.newCars;
const gtmComponentName = LBX_GTM_TRACKING_VALUES.componentName.lbxExperienceHeader;

const { MEDIA_TYPES: { VIDEO }, INJECTION_ELEMENTS } = constants;

export function VideoHero(props) {

  const {
    title = '',
    subtitle,
    afternoonGreeting,
    morningGreeting,
    eveningGreeting,
    backgroundVideoUrl,
    tailorYourExperience,
    redefineYourExperience,
    assets,
    firstName,
    atmosphere,
    isInjected,
    navigateToStyleSelector
  } = props;

  const videoUrl = _.get(assets, `${atmosphere}.backgroundVideoUrl`, backgroundVideoUrl);
  const images = [
    { image: _.get(assets, `${atmosphere}.imageOne`), id: _.get(assets, `${atmosphere}.imageOneId`)},
    { image: _.get(assets, `${atmosphere}.imageTwo`), id: _.get(assets, `${atmosphere}.imageTwoId`)}
  ];
  const rootElRef = useRef();
  const windowSize = useWindowSize();
  const heightOffset = isInjected ? useExternalElementSize(INJECTION_ELEMENTS.HEADER_SELECTOR).height : 0;
  const { marginOffset, viewportMultiplier } = useMemo(() => getHeightParams(windowSize.width), [windowSize.width]);
  const displayHeight = useMemo(() => (windowSize.height - heightOffset - 2 * marginOffset) * viewportMultiplier, [windowSize]);
  const greeting = getGreeting({ afternoonGreeting, morningGreeting, eveningGreeting, firstName });
  const hasQuizBeenTaken = atmosphere != "default";
  const styleSelectorPromptText =  hasQuizBeenTaken ? redefineYourExperience : tailorYourExperience; 
  const block = 'c-video-hero';

  return (
    <section className={`${block} container-fluid`} ref={rootElRef}>
      <div className={`${block}__background-cover`} style={{ height: displayHeight }}>
        <CssAnimation
          enter={{ name: 'fade-in', duration: 2 }}
          triggerEnter={true}
          renderView={!!videoUrl}
        >
          <div>
            <BackgroundMedia
              mediaType={VIDEO}
              mediaUrl={videoUrl}
              overlay={true}
            />
          </div>
        </CssAnimation>
        <div className={`${block}__greeting`}>
          <AnimatedTextHeader
            heading={title}
            subheading={subtitle}
            copy={greeting}
            blockClasses='dark-theme theme-content-only'
            headingClasses='headline-var-1'
            copyClasses='h3'
            subheadingExpandedWidth={true}
          />
        </div>
      </div>
      <CssAnimation
        enter={{ name: 'short-slide-up-fade-in', duration: 1 }}
        triggerEnter={true}
        renderView={Array.isArray(images)}
      >
        <div className={`${block}__images-container container-xxxl`} style={{ height: displayHeight }}>
          <div className={`${block}__images-inner-container`}>
            { 
              images[0] && images[0].image &&
                <div className={`${block}__image ${block}__image--image-1 col-2 offset-1 offset-xl-neg-1`}>
                  <AtmosphereImage 
                  base={images[0].image} 
                  animationDelay={0.5}
                  showExpandIcon={true}
                  id={images[0]?.id} 
                  gtmComponentGroup={gtmComponentGroup}
                  gtmComponentName={gtmComponentName}
                  />
                </div>
            }
            {
              images[1] && images[1].image &&
                <div className={`${block}__image ${block}__image--image-2 col-2 offset-9 offset-xl-11`}>
                  <AtmosphereImage 
                  base={images[1].image}
                  animationDelay={0.01}
                  showExpandIcon={true}
                  id={images[1]?.id} 
                  gtmComponentGroup={gtmComponentGroup}
                  gtmComponentName={gtmComponentName}
                  />
                </div>
            }
          </div>
        </div>
      </CssAnimation>

      {styleSelectorPromptText && 
        <CssAnimation
          enter={{ name: 'slide-up-fade-in', duration: 0.5, delay: '1.25, hidden' }}
          triggerEnter={true}
          renderView={!!styleSelectorPromptText}
        >
          <div className={`${block}__prompt container apply-theme-background`}>
            <h3 className={`${block}__prompt_title h5`}>{styleSelectorPromptText?.heading}</h3>
            <p>{styleSelectorPromptText?.copy}</p>
            <div className={`${block}__start-experience`}>
              <Button {...{
                id: styleSelectorPromptText?.link?.id,
                label: styleSelectorPromptText?.link?.label,
                testId: styleSelectorPromptText?.link?.id,
                type: styleSelectorPromptText?.link?.type,
                onClickCallback: () => {
                  navigateToStyleSelector()
                },
                parentBlock: block,
                gtmComponentGroup: gtmComponentGroup,
                gtmComponentName: gtmComponentName,
                gtmLabel: CORE_GTM_TRACKING_VALUES.label.personalise
              }} />
            </div>
            
          </div>
        </CssAnimation>      
      }
    </section>
  )
};